import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DownloadIcon from "@mui/icons-material/Download";
import DateFilterButtons from "../../components/DateFilterButtons";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import {
  Alert,
  Box,
  ButtonProps,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { COLORS } from "../../assets/theme";
import ToastNotification from "../../components/ToastNotification";
import CustomButton from "../../components/common/CustomButton";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import CustomSwitch from "../../components/common/CustomSwitch";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import SearchForm from "../../components/common/SearchForm";
import AutoComplete, {
  IAutoCompleteProps,
} from "../../components/common/AutoComplete";
import { ExportAttachmentApi } from "../../services/ExportService";
import { useProfileOptionsQuery } from "../../services/ProfileCostImportConfigService";
import {
  PendingCostImport,
  PendingCostImportRow,
  PendingCostImportParams,
  PendingImportStatus,
  VendorOption,
  confirmPendingCostImport,
  deletePendingCostImport,
  fetchPendingCostImportRows,
  rejectPendingCostImport,
  usePendingCostImportQuery,
  usePendingCostImportsQuery,
  useVendorsForProfileQuery,
} from "../../services/PendingCostImportService";
import { fetchProvider } from "../../services/ProviderService";
import { IField, Params } from "../../types/table/interfaces.type";
import { useQuery } from "@tanstack/react-query";
import { hasJwtPermission } from "../../services/ServicesHelpers";

type StatusFilter = PendingImportStatus | "ALL";

const STATUS_TABS: { label: string; value: StatusFilter }[] = [
  { label: "All", value: "ALL" },
  { label: "Pending", value: "PENDING" },
  { label: "Confirmed", value: "CONFIRMED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Failed", value: "FAILED" },
  { label: "Expired", value: "EXPIRED" },
];

const iso8601DateTimeRegex =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;

function formatUtcDateTime(value?: string | null): string {
  if (!value) return "-";
  if (!iso8601DateTimeRegex.test(value)) return value;
  try {
    return formatInTimeZone(parseISO(value), "UTC", "dd-MM-yyyy HH:mm:ss a");
  } catch {
    return value;
  }
}

function statusChip(status: PendingImportStatus) {
  const config: Record<
    PendingImportStatus,
    { label: string; bg: string; color: string; border: string }
  > = {
    PENDING: {
      label: "PENDING",
      bg: "#FFF3CD",
      color: "#856404",
      border: "#FFEEAA",
    },
    CONFIRMED: {
      label: "CONFIRMED",
      bg: "#D4EDDA",
      color: "#155724",
      border: "#C3E6CB",
    },
    REJECTED: {
      label: "REJECTED",
      bg: "#F8D7DA",
      color: "#721C24",
      border: "#F5C6CB",
    },
    FAILED: {
      label: "FAILED",
      bg: "#F8D7DA",
      color: "#721C24",
      border: "#F5C6CB",
    },
    EXPIRED: {
      label: "EXPIRED",
      bg: "#E2E3E5",
      color: "#383D41",
      border: "#D6D8DB",
    },
  };
  const s = config[status] ?? config.PENDING;
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        backgroundColor: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        fontWeight: 700,
        letterSpacing: "0.3px",
        pointerEvents: "none",
      }}
    />
  );
}

const VendorNameCell: React.FC<{
  vendorConnectionProfileId?: number | null;
  detectedSystemId?: string;
}> = ({ vendorConnectionProfileId, detectedSystemId }) => {
  const q = useQuery({
    queryKey: ["vendor", vendorConnectionProfileId],
    queryFn: () => fetchProvider(String(vendorConnectionProfileId)),
    enabled: Boolean(vendorConnectionProfileId),
  });

  if (!vendorConnectionProfileId) {
    return <span>{detectedSystemId || "-"}</span>;
  }

  if (q.isPending) return <span>...</span>;

  if (q.data) {
    return <span>{`${q.data.name} (${q.data.systemId})`}</span>;
  }

  return <span>{detectedSystemId || "-"}</span>;
};

interface ConfirmDialogState {
  action: "reject" | "delete";
  id: string;
}

const PendingCostImportDetailDialog: React.FC<{
  open: boolean;
  pendingImportId: string;
  profileMap: Record<string, string>;
  onClose: () => void;
  onRefresh: () => void;
}> = ({ open, pendingImportId, profileMap, onClose, onRefresh }) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [showErrorRowsOnly, setShowErrorRowsOnly] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(
    null
  );
  const [selectedVendorId, setSelectedVendorId] = useState<string>("");
  const [rowsSearch, setRowsSearch] = useState<string>("");

  const { data, isPending, error } = usePendingCostImportQuery(pendingImportId);

  const { data: vendors = [], isLoading: vendorsLoading } =
    useVendorsForProfileQuery(data?.profileId || "");

  const rowsFields: IField<PendingCostImportRow>[] = useMemo(
    () => [
      { key: "rowNumber", header: "Row Number" },
      {
        key: "rawCountry",
        header: "Country",
        transform: (row) => row.rawCountry || "-",
      },
      {
        key: "rawOperator",
        header: "Operator",
        transform: (row) => row.rawOperator || "-",
      },
      {
        key: "rawMccmnc",
        header: "Raw MCC/MNC",
        transform: (row) =>
          row.rawMccmnc ||
          [row.rawMcc, row.rawMnc].filter(Boolean).join("/") ||
          "-",
      },
      {
        key: "cost",
        header: "Cost",
        transform: (row) =>
          row.cost === undefined || row.cost === null
            ? "-"
            : Number(row.cost)
                .toFixed(6)
                .replace(/\.?0+$/, ""),
      },
      { key: "effectiveDate", header: "Effective Date" },
      { key: "errorMessage", header: "Error Message" },
    ],
    []
  );

  const fetchRows = async (
    params: Params<PendingCostImportRow>
  ): Promise<any> => {
    if (!pendingImportId) {
      return {
        content: [],
        number: 0,
        size: params.size ?? 20,
        totalElements: 0,
        totalPages: 0,
      };
    }
    return await fetchPendingCostImportRows(
      pendingImportId,
      params,
      showErrorRowsOnly ? true : undefined
    );
  };

  useEffect(() => {
    setRowsSearch("");
    setSelectedVendorId("");
  }, [pendingImportId]);

  const handleConfirm = async () => {
    if (!pendingImportId) return;
    try {
      await confirmPendingCostImport(
        pendingImportId,
        vendorNotDetected ? selectedVendorId || undefined : undefined
      );
      ToastNotification.success("Import confirmed successfully!");
      onRefresh();
      queryClient.invalidateQueries({ queryKey: ["pending-cost-import"] });
      queryClient.invalidateQueries({
        queryKey: ["pending-cost-import", pendingImportId],
      });
      onClose();
    } catch (e: any) {
      ToastNotification.error(e.message || "Failed to confirm import.");
    }
  };

  const handleReject = async () => {
    if (!pendingImportId) return;
    try {
      await rejectPendingCostImport(pendingImportId);
      ToastNotification.success("Import rejected successfully!");
      onRefresh();
      queryClient.invalidateQueries({ queryKey: ["pending-cost-import"] });
      queryClient.invalidateQueries({
        queryKey: ["pending-cost-import", pendingImportId],
      });
      onClose();
    } catch (e: any) {
      ToastNotification.error(e.message || "Failed to reject import.");
    }
  };

  const handleDelete = async () => {
    if (!pendingImportId) return;
    try {
      await deletePendingCostImport(pendingImportId);
      ToastNotification.success("Import deleted successfully!");
      onRefresh();
      queryClient.invalidateQueries({ queryKey: ["pending-cost-import"] });
      onClose();
    } catch (e: any) {
      ToastNotification.error(e.message || "Failed to delete import.");
    }
  };

  const title = data
    ? `Pending Cost Import - ${profileMap[data.profileId] || data.profileId}`
    : "Pending Cost Import";

  const vendorNotDetected = data?.vendorConnectionProfileId == null;
  const isFailed = data?.status === "FAILED";
  const isPendingStatus = data?.status === "PENDING";
  const canExportAttachment = hasJwtPermission("DOWNLOAD_PENDING_COST_IMPORT_FILE");

  const handleDownloadAttachment = async () => {
    if (!canExportAttachment) return;
    if (!data?.attachmentId) {
      ToastNotification.error("No attachment available to download.");
      return;
    }
    try {
      await ExportAttachmentApi(String(data.attachmentId), "cost-import");
      ToastNotification.success("File downloaded successfully!");
    } catch (e: any) {
      ToastNotification.error(e.message || "Failed to download attachment.");
    }
  };

  return (
    <>
      <CustomDialog
        open={open}
        onClose={onClose}
        mode="confirm"
        type="confirm"
        title={title}
        contentSx={{ overflowX: "hidden" }}
        fields={[
          {
            type: "custom",
            component: (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  width: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                }}
              >
                {error ? (
                  <Alert severity="error">
                    {String((error as any)?.message || error)}
                  </Alert>
                ) : null}

                {isPending ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 160,
                    }}
                  >
                    <CircularProgress sx={{ color: COLORS.GOLD }} />
                  </Box>
                ) : data ? (
                  <>
                    {vendorNotDetected ? (
                      <Alert severity="warning">
                        <Box sx={{ width: "100%", minWidth: 0 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Vendor account could not be detected automatically
                          </Typography>
                          {vendors.length > 0 && (
                            <Box sx={{ mt: 2, width: "100%", minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 600 }}
                              >
                                Please select the vendor account:
                              </Typography>
                              <AutoComplete
                                label="Vendor"
                                value={selectedVendorId}
                                options={vendors.map((vendor) => ({
                                  label: `${vendor.name} (${vendor.systemId})`,
                                  value: vendor.id,
                                }))}
                                clearable
                                placeholder="Select Vendor"
                                disabled={vendorsLoading}
                                onChange={(value) => {
                                  if (Array.isArray(value)) {
                                    setSelectedVendorId(String(value[0] ?? ""));
                                    return;
                                  }
                                  setSelectedVendorId(String(value ?? ""));
                                }}
                                sx={{ width: "100%", minWidth: 0 }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Alert>
                    ) : null}
                    {isFailed && data.errorMessage ? (
                      <Alert severity="error">{data.errorMessage}</Alert>
                    ) : null}

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: isSmallScreen ? "1fr" : "1fr 1fr",
                        gap: 1.5,
                        width: "100%",
                        minWidth: 0,
                        boxSizing: "border-box",
                      }}
                    >
                      <MetadataItem label="Status" value={data.status} />
                      <MetadataItem
                        label="Profile"
                        value={profileMap[data.profileId] || data.profileId}
                      />
                      <MetadataItem
                        label="Vendor/SystemId"
                        value={
                          <VendorNameCell
                            vendorConnectionProfileId={
                              data.vendorConnectionProfileId
                            }
                            detectedSystemId={data.detectedSystemId}
                          />
                        }
                      />
                      <MetadataItem
                        label="File Name"
                        value={
                          data.attachmentId ? (
                            <Box
                              onClick={
                                canExportAttachment
                                  ? handleDownloadAttachment
                                  : undefined
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                color: canExportAttachment
                                  ? COLORS.GOLD
                                  : "#D3D3D3",
                                "&:hover": canExportAttachment
                                  ? {
                                      textDecoration: "underline",
                                    }
                                  : undefined,
                              }}
                            >
                              <DownloadIcon
                                sx={{
                                  fontSize: "1rem",
                                  color: canExportAttachment
                                    ? COLORS.GOLD
                                    : "#D3D3D3",
                                }}
                              />
                              <span>{data.fileName || "-"}</span>
                            </Box>
                          ) : (
                            data.fileName || "-"
                          )
                        }
                      />
                      <MetadataItem
                        label="Email Subject"
                        value={data.emailSubject || "-"}
                      />
                      <MetadataItem
                        label="Sender Address"
                        value={data.emailSenderAddress || "-"}
                      />
                      <MetadataItem
                        label="Received At"
                        value={formatUtcDateTime(data.receivedAt)}
                      />
                      <MetadataItem
                        label="Processed At"
                        value={formatUtcDateTime(data.processedAt)}
                      />
                      <MetadataItem
                        label="Processed By"
                        value={data.processedBy || "-"}
                      />
                      <MetadataItem
                        label="Override Existing"
                        value={data.overrideExisting ? "Yes" : "No"}
                      />
                      <MetadataItem
                        label="Rate Count"
                        value={String(data.rateCount)}
                      />
                      <MetadataItem
                        label="Rows (Total / Valid / Error)"
                        value={`${data.totalRows ?? "-"} / ${
                          data.validRows ?? "-"
                        } / ${data.errorRows ?? "-"}`}
                      />
                    </Box>

                    <Divider />

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: isSmallScreen ? "stretch" : "center",
                        justifyContent: "space-between",
                        flexDirection: isSmallScreen ? "column" : "row",
                        gap: 1.5,
                      }}
                    >
                      <Typography
                        sx={{ fontWeight: 600, color: COLORS.typographyColor }}
                      >
                        Parsed Rows Preview
                      </Typography>
                      <Box sx={{ maxWidth: isSmallScreen ? "100%" : 220 }}>
                        <CustomSwitch
                          label="Only error rows"
                          activeText="Errors"
                          inactiveText="All"
                          checked={showErrorRowsOnly}
                          onChange={(e) =>
                            setShowErrorRowsOnly(e.target.checked)
                          }
                        />
                      </Box>
                    </Box>

                    <SearchHeaderContainer
                      search={rowsSearch}
                      setSearch={setRowsSearch}
                      debounceMs={500}
                    />

                    <Box sx={{ width: "100%", minWidth: 0, overflowX: "auto" }}>
                      <CustomTable<PendingCostImportRow>
                        fullBleedMobile={false}
                        key={`${pendingImportId}-${
                          showErrorRowsOnly ? "errors" : "all"
                        }`}
                        filters={{ search: rowsSearch } as any}
                        fields={rowsFields}
                        fetchFunc={fetchRows}
                        shadeRow={(row) => row.hasError}
                        shadeBg={(row) =>
                          row.hasError ? "#F8D7DA" : undefined
                        }
                        shadeColor={COLORS.typographyColor}
                        sortableFields={rowsFields.map((field) => field.key)}
                        initialSort={{ field: "rowNumber", direction: "asc" }}
                      />
                    </Box>
                  </>
                ) : null}
              </Box>
            ),
          },
        ]}
        buttons={
          isPendingStatus
            ? [
                {
                  label: "Confirm",
                  variant: "contained",
                  bgColor: hasJwtPermission("CONFIRM_PENDING_COST_IMPORT") ? COLORS.GOLD : "#D3D3D3",
                  borderRadius: "12px",
                  onClick: handleConfirm,
                  disabled: vendorNotDetected && !selectedVendorId,
                  permission: "CONFIRM_PENDING_COST_IMPORT",
                },
                {
                  label: "Reject",
                  variant: "outlined",
                  bgColor: "transparent",
                  textColor: hasJwtPermission("REJECT_PENDING_COST_IMPORT") ? COLORS.GOLD : "#D3D3D3",
                  borderColor: hasJwtPermission("REJECT_PENDING_COST_IMPORT") ? COLORS.GOLD : "#D3D3D3",
                  borderRadius: "12px",
                  onClick: () =>
                    setConfirmDialog({ action: "reject", id: pendingImportId }),
                  permission: "REJECT_PENDING_COST_IMPORT",
                },
                {
                  label: "Delete",
                  variant: "contained",
                  bgColor: hasJwtPermission("DELETE_PENDING_COST_IMPORT") ? COLORS.errorRed : "#D3D3D3",
                  borderRadius: "12px",
                  onClick: () =>
                    setConfirmDialog({ action: "delete", id: pendingImportId }),
                  permission: "DELETE_PENDING_COST_IMPORT",
                },
              ]
            : []
        }
      />

      <Dialog
        open={Boolean(confirmDialog)}
        onClose={() => setConfirmDialog(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {confirmDialog?.action === "reject"
            ? "Reject Import"
            : "Delete Import"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: COLORS.typographyColor }}>
            {confirmDialog?.action === "reject"
              ? "Are you sure you want to reject this pending import?"
              : "Are you sure you want to delete this pending import and all its rows?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton
            label="Cancel"
            variant="outlined"
            bgColor="transparent"
            textColor={COLORS.typographyColor}
            borderColor={COLORS.dividerColor}
            borderRadius="12px"
            onClick={() => setConfirmDialog(null)}
          />
          <CustomButton
            label={confirmDialog?.action === "reject" ? "Reject" : "Delete"}
            variant="contained"
            bgColor={
              confirmDialog?.action === "reject"
                ? COLORS.errorRed
                : COLORS.errorRed
            }
            borderRadius="12px"
            onClick={async () => {
              const action = confirmDialog?.action;
              setConfirmDialog(null);
              if (action === "reject") {
                await handleReject();
              } else if (action === "delete") {
                await handleDelete();
              }
            }}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

const MetadataItem: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => {
  return (
    <Box
      sx={{
        backgroundColor: COLORS.White,
        border: `1px solid ${COLORS.dividerColor}`,
        borderRadius: 2,
        padding: 1.25,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.8rem",
          color: COLORS.placeHolderColor,
          fontWeight: 600,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", color: COLORS.typographyColor }}>
        {value || "-"}
      </Typography>
    </Box>
  );
};

function getTodayDateRange(): { fromDate: Date; toDate: Date; rangeString: string } {
  const today = new Date();
  const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0));
  const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + 1, 0, 0, 0, 0));
  const fmt = (d: Date) => {
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}T00:00:00.000Z`;
  };
  return { fromDate: start, toDate: end, rangeString: `${fmt(start)}_${fmt(end)}` };
}

const PendingCostImportsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const todayRange = React.useMemo(() => getTodayDateRange(), []);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("PENDING");
  const [filters, setFilters] = useState<PendingCostImportParams>({
    search: "",
    status: "PENDING",
    fromDate: todayRange.fromDate,
    toDate: todayRange.toDate,
  });
  const [appliedFilters, setAppliedFilters] = useState<PendingCostImportParams>(
    {
      search: "",
      status: "PENDING",
      fromDate: todayRange.fromDate,
      toDate: todayRange.toDate,
    }
  );

  const [fromDate, setFromDate] = useState<Date | null>(todayRange.fromDate);
  const [toDate, setToDate] = useState<Date | null>(todayRange.toDate);

  const [selectedId, setSelectedId] = useState<string>("");
  const [detailOpen, setDetailOpen] = useState(false);

  const { data: profileOptionsData, error: profilesError } =
    useProfileOptionsQuery();

  const profileMap = useMemo(
    () => profileOptionsData?.profileMap ?? {},
    [profileOptionsData]
  );

  const profileOptions = useMemo(
    () => profileOptionsData?.options ?? [],
    [profileOptionsData]
  );

  React.useEffect(() => {
    if (profilesError) {
      ToastNotification.error(profilesError.message);
    }
  }, [profilesError]);

  const fields: IField<PendingCostImport>[] = useMemo(
    () => [
      {
        key: "status",
        header: "Status",
        transform: (row) => statusChip(row.status),
      },
      {
        key: "profileId",
        header: "Profile Name",
        transform: (row) => profileMap[row.profileId] || row.profileId,
      },
      {
        key: "detectedSystemId",
        header: "Vendor/SystemId",
        transform: (row) => (
          <VendorNameCell
            vendorConnectionProfileId={row.vendorConnectionProfileId}
            detectedSystemId={row.detectedSystemId}
          />
        ),
      },
      { key: "fileName", header: "File Name" },
      { key: "emailSubject", header: "Email Subject" },
      { key: "emailSenderAddress", header: "Sender Address" },
      { key: "receivedAt", header: "Received At" },
      {
        key: "totalRows",
        header: "Total / Valid / Error",
        transform: (row) =>
          `${row.totalRows ?? "-"} / ${row.validRows ?? "-"} / ${
            row.errorRows ?? "-"
          }`,
      },
    ],
    [profileMap]
  );

  const handleStatusChange = (_: any, value: StatusFilter) => {
    setStatusFilter(value);
    setFilters((prev) => ({
      ...prev,
      status: value === "ALL" ? undefined : value,
    }));
    setAppliedFilters((prev) => ({
      ...prev,
      status: value === "ALL" ? undefined : value,
    }));
  };

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleDateFilterSelect = (option: string) => {
    if (option) {
      const [startStr, endStr] = option.split("_");
      setFromDate(new Date(startStr));
      setToDate(new Date(endStr));
      setFilters((prev) => ({
        ...prev,
        fromDate: new Date(startStr),
        toDate: new Date(endStr),
      }));
    } else {
      setFromDate(null);
      setToDate(null);
      setFilters((prev) => ({ ...prev, fromDate: undefined, toDate: undefined }));
    }
  };

  const handleSearchClick = () => {
    setAppliedFilters({ ...filters, fromDate: fromDate ?? undefined, toDate: toDate ?? undefined });
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["pending-cost-import"] });
  };

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Profile",
      value: filters.profileId ?? "",
      options: profileOptions,
      clearable: true,
      placeholder: "Select Profile",
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          profileId: value ? String(value) : undefined,
        }));
      },
    },
  ];

  const isSearchDisabled = !Object.values(filters).some((value) => {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return value !== undefined && value !== null;
  });
  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      disabled: isSearchDisabled,
      permission: "GET_ALL_PENDING_COST_IMPORTS",
    },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Pending Cost Imports"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />

      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <Box display="flex" flexDirection="column" gap={2} width="100%">
              <Box display="flex" flexDirection="column" gap={2} width="100%">
                <DateFilterButtons
                  onSelect={handleDateFilterSelect}
                  selectedOption="Today"
                  fromDate={fromDate}
                  toDate={toDate}
                />
                <CustomDateRangePicker
                  fromDate={fromDate}
                  toDate={toDate}
                  setFromDate={setFromDate}
                  setToDate={setToDate}
                />
              </Box>
            </Box>
          }
          autoCompletes={autoCompletes}
          buttons={buttons}
        />
      </Box>

      <Box
        sx={{
          backgroundColor: COLORS.White,
          borderRadius: 2,
          border: `1px solid ${COLORS.dividerColor}`,
          mb: 2,
        }}
      >
        <Tabs
          value={statusFilter}
          onChange={handleStatusChange}
          variant={isSmallScreen ? "scrollable" : "standard"}
          scrollButtons={isSmallScreen ? "auto" : false}
          sx={{
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
            "& .MuiTabs-indicator": { backgroundColor: COLORS.GOLD },
            "& .MuiTab-root.Mui-selected": { color: COLORS.GOLD },
          }}
        >
          {STATUS_TABS.map((t) => (
            <Tab key={t.value} label={t.label} value={t.value} />
          ))}
        </Tabs>
      </Box>

      <Box mb={2}>
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={handleSearchChange}
        />

        <CustomTable<PendingCostImport>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={usePendingCostImportsQuery}
          onRowClick={
            hasJwtPermission("GET_PENDING_COST_IMPORT")
              ? (row) => {
                  setSelectedId(row.id);
                  setDetailOpen(true);
                }
              : undefined
          }
          shadeRow={(row) => row.status === "FAILED"}
          shadeBg={(row) => (row.status === "FAILED" ? "#F8D7DA" : undefined)}
          shadeColor={COLORS.typographyColor}
          sortableFields={["receivedAt", "status"]}
        />

        <Box mt={1} display="flex" justifyContent="flex-end">
          <CustomButton
            label="Refresh"
            variant="outlined"
            bgColor="transparent"
            textColor={COLORS.typographyColor}
            borderColor={COLORS.dividerColor}
            borderRadius="12px"
            onClick={handleRefresh}
            permission="GET_ALL_PENDING_COST_IMPORTS"
          />
        </Box>
      </Box>

      <PendingCostImportDetailDialog
        open={detailOpen}
        pendingImportId={selectedId}
        profileMap={profileMap}
        onClose={() => setDetailOpen(false)}
        onRefresh={handleRefresh}
      />
    </Box>
  );
};

export default PendingCostImportsPage;
